/**
 * Coach-related configuration constants
 * Centralized to avoid duplication across components
 * These should match the API constants in medley-api/constants/coach.ts
 */

// Coach statuses (MED-683: derived Active/Inactive from modality_approvals + manually_inactive)
export const COACH_STATUSES = ['active', 'inactive'] as const;
export type CoachStatus = (typeof COACH_STATUSES)[number];

export const COACH_STATUS_CONFIG: Record<CoachStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'medleyGreen' },
  inactive: { label: 'Inactive', color: 'gray' },
};

// Predefined expertise areas (legacy - for expertise_areas field)
export const PREDEFINED_EXPERTISE = [
  'Executive Coaching',
  'Leadership Development',
  'Team Building',
  'Communication Skills',
  'Career Development',
  'Performance Management',
  'Conflict Resolution',
  'Change Management',
  'Emotional Intelligence',
  'Strategic Thinking',
] as const;
export type ExpertiseArea = (typeof PREDEFINED_EXPERTISE)[number];

// Industry options (~35 values)
export const INDUSTRY_OPTIONS = [
  'Aerospace & Defense',
  'Agriculture',
  'Automotive',
  'Banking & Finance',
  'Biotechnology',
  'Chemicals',
  'Construction',
  'Consumer Goods',
  'Education',
  'Energy & Utilities',
  'Entertainment & Media',
  'Financial Services',
  'Food & Beverage',
  'Government',
  'Healthcare',
  'Hospitality & Tourism',
  'Human Resources',
  'Information Technology',
  'Insurance',
  'Legal Services',
  'Logistics & Supply Chain',
  'Manufacturing',
  'Marketing & Advertising',
  'Mining & Metals',
  'Non-Profit',
  'Pharmaceuticals',
  'Professional Services',
  'Real Estate',
  'Retail',
  'Sports & Recreation',
  'Telecommunications',
  'Transportation',
  'Venture Capital & Private Equity',
  'Other',
] as const;
export type IndustryOption = (typeof INDUSTRY_OPTIONS)[number];

// Race/Ethnicity options
export const RACE_ETHNICITY_OPTIONS = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Hispanic or Latino',
  'Middle Eastern or North African',
  'Native Hawaiian or Other Pacific Islander',
  'White',
  'Two or More Races',
  'Prefer not to say',
  'Other',
] as const;
export type RaceEthnicityOption = (typeof RACE_ETHNICITY_OPTIONS)[number];

// Gender identity options
export const GENDER_IDENTITY_OPTIONS = [
  'Woman',
  'Man',
  'Non-binary',
  'Genderqueer',
  'Genderfluid',
  'Agender',
  'Prefer not to say',
  'Other',
] as const;
export type GenderIdentityOption = (typeof GENDER_IDENTITY_OPTIONS)[number];

// Pronouns options
export const PRONOUNS_OPTIONS = [
  'she/her',
  'he/him',
  'they/them',
  'she/they',
  'he/they',
  'Other',
] as const;
export type PronounsOption = (typeof PRONOUNS_OPTIONS)[number];

// Learning experience options
export const LEARNING_EXPERIENCE_OPTIONS = [
  'Workshops',
  'Self-paced courses',
  'Role-playing exercises',
  'Peer coaching circles',
  'Masterminds',
  'One-on-one mentoring',
  'Reading materials',
  'Video content',
  'Live webinars',
  'Conferences',
] as const;
export type LearningExperienceOption = (typeof LEARNING_EXPERIENCE_OPTIONS)[number];

// Certification options
export const CERTIFICATION_OPTIONS = [
  'ICF - ACC',
  'ICF - PCC',
  'ICF - MCC',
  'Other',
  'In Progress',
] as const;
export type CertificationOption = (typeof CERTIFICATION_OPTIONS)[number];

// Modality proficiency options (match API values)
export const MODALITY_PROFICIENCY_OPTIONS = [
  { value: 'group_coaching', label: 'Group Coaching' },
  { value: 'one_on_one', label: '1:1 Coaching' },
  { value: 'workshop', label: 'Workshop' },
] as const;

// Timezones moved to config/timezones.ts and are consumed through the shared
// <TimezoneSelect /> in components/common. The old 13-entry list here covered
// no part of India, so a coach based there had to be recorded as Europe/London.
